import type { CompetitionSources } from '@/api/competitionDetail'
import { ATTRIBUTION_MODEL_LABELS } from '@/api/types'
import { ModelSelect } from '@/components/ModelSelect'
import { EmptyState, formatCurrency, formatInteger, formatPercent, type FormatOptions } from '@/design'
import { useModel } from '@/features/marketing/attribution/attributionQuery'
import type { GlobalFilters } from '@/lib/filters'

import { TabRead } from '../pieces'
import { useCompetitionSources } from '../queries'
import { UNDEFINED_FIGURE } from '../reasons'

/** A touch field the touch did not have, as stored. */
const NONE = '(none)'

/**
 * The competition's orders, tickets and revenue by the channel an attribution model
 * credits them to (`…/sources?model=`), read when the tab opens and again per model:
 * each channel, then the touches' own source, medium and campaign under it. Orders no
 * touch is credited with are the unattributed row, last. Commerce is never
 * consent-gated, so every order of the period is here.
 */
export function SourcesTab({ competitionId, filters, money }: { competitionId: string; filters: GlobalFilters; money: FormatOptions }) {
  const [model, setModel] = useModel()
  const read = useCompetitionSources(competitionId, filters, model)
  return (
    <div className="cd-stack">
      <div className="cd-controls">
        <ModelSelect value={model} onChange={setModel} />
      </div>
      <TabRead read={read} loading="Loading marketing sources">
        {(sources) => <SourcesBody sources={sources} money={{ ...money, currency: sources.currency }} />}
      </TabRead>
    </div>
  )
}

function SourcesBody({ sources, money }: { sources: CompetitionSources; money: FormatOptions }) {
  const { channels, totals, model } = sources
  if (channels.length === 0) {
    return (
      <EmptyState
        variant="no-results"
        compact
        title="No orders in this period"
        description="No order with a ticket for this competition completed in this period, so no channel is credited."
      />
    )
  }
  const currency = (value: number) => formatCurrency(value, money)
  const caption = `Orders by channel, ${ATTRIBUTION_MODEL_LABELS[model].toLowerCase()} model`
  return (
    <div className="ci-table-wrap" role="region" aria-label={caption} tabIndex={0}>
      <table className="ci-table cd-sources">
        <caption className="ci-visually-hidden">{caption}</caption>
        <thead>
          <tr>
            <th scope="col">Channel and source</th>
            <th scope="col" data-align="end">
              Orders
            </th>
            <th scope="col" data-align="end" className="cd-optional">
              Tickets
            </th>
            <th scope="col" data-align="end">
              Revenue
            </th>
            <th scope="col" data-align="end">
              Share
            </th>
          </tr>
        </thead>
        {channels.map((channel) => (
          <tbody key={channel.channel ?? 'unattributed'} data-channel={channel.channel ?? 'unattributed'}>
            <tr className="cd-sources__channel">
              <th scope="row">{channel.label}</th>
              <td data-align="end">{formatInteger(channel.orders)}</td>
              <td data-align="end" className="cd-optional">
                {formatInteger(channel.tickets)}
              </td>
              <td data-align="end">{currency(channel.revenue)}</td>
              <td data-align="end">{channel.revenueShare === null ? UNDEFINED_FIGURE : formatPercent(channel.revenueShare)}</td>
            </tr>
            {channel.channel === null
              ? null
              : channel.sources.map((source) => (
                  <tr key={`${source.source}|${source.medium}|${source.campaign}`} className="cd-sources__source">
                    <th scope="row">
                      <span className="ci-visually-hidden">{channel.label}: </span>
                      {source.source ?? NONE} / {source.medium ?? NONE}
                      {source.campaign ? <span className="cd-sources__campaign"> · {source.campaign}</span> : null}
                    </th>
                    <td data-align="end">{formatInteger(source.orders)}</td>
                    <td data-align="end" className="cd-optional">
                      {formatInteger(source.tickets)}
                    </td>
                    <td data-align="end">{currency(source.revenue)}</td>
                    <td data-align="end" />
                  </tr>
                ))}
          </tbody>
        ))}
        <tfoot>
          <tr>
            <th scope="row">Total</th>
            <td data-align="end">{formatInteger(totals.orders)}</td>
            <td data-align="end" className="cd-optional">
              {formatInteger(totals.tickets)}
            </td>
            <td data-align="end">{currency(totals.revenue)}</td>
            <td data-align="end" />
          </tr>
        </tfoot>
      </table>
    </div>
  )
}
